/*
 * Sale pieces (sections/sale-*.liquid, snippets/sale-*.liquid): the November sale's home page and product page parts.
 *   - [data-from][data-until]: shown only inside that window (epoch seconds; 0 = open). [data-pick-first] shows only
 *     the first child that's in its window (e.g. one announcement per phase). The server already decides; this
 *     re-checks with the browser clock (cached pages) and honours ?preview_date=2026-11-13 for previews.
 *   - <sale-countdown data-end="…">: days / hrs / min / sec boxes counting down to a real time.
 *   - [data-sale-remind] forms: "remind me at 8am" email sign-up to a Klaviyo list.
 *   - [data-sale-video]: plays the announcement video in a pop-up.
 *   - [data-set-add]: "+ add to set" on product cards, fills the bundle builder on the same page.
 *   - [data-pair-add]: "add both" on the free-tee pairs.
 */
(() => {
  if (window.sundaySale) return;

  // "Now", or the day being previewed (?preview_date=2026-11-13 or 2026-11-13T09:00)
  const preview = new URLSearchParams(location.search).get('preview_date');
  const offset = document.documentElement.dataset.shopOffset || '+11:00';
  // Theme editor: Theme settings → Sale → Preview date (the clock runs on from it, so countdowns still tick)
  const editorStart = Number(document.documentElement.dataset.previewNow) * 1000;
  const loadedAt = Date.now();
  const now = () => {
    if (preview) return Date.parse(preview.length <= 10 ? `${preview}T12:00:00${offset}` : `${preview}${preview.match(/[+-]\d\d:?\d\d$|Z$/) ? '' : offset}`);
    if (editorStart) return editorStart + (Date.now() - loadedAt);
    return Date.now();
  };

  const inWindow = (el) => {
    const t = now();
    const from = Number(el.dataset.from) * 1000;
    const until = Number(el.dataset.until) * 1000;
    return (!from || t >= from) && (!until || t < until);
  };

  function applyWindows(scope = document) {
    for (const group of scope.querySelectorAll('[data-pick-first]')) {
      let picked = false;
      for (const child of group.querySelectorAll(':scope > [data-from]')) {
        const show = !picked && inWindow(child);
        child.hidden = !show;
        if (show) picked = true;
      }
    }
    for (const el of scope.querySelectorAll('[data-from]:not([data-pick-first] > [data-from])')) el.hidden = !inWindow(el);
  }

  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    tick() {
      let left = Math.max(0, Math.floor((this.end - now()) / 1000));
      const parts = { d: Math.floor(left / 86400), h: Math.floor((left % 86400) / 3600), m: Math.floor((left % 3600) / 60), s: left % 60 };
      if (!this.querySelector('[data-unit="d"]')) parts.h += parts.d * 24; // no days box: show total hours
      for (const [unit, value] of Object.entries(parts)) {
        const box = this.querySelector(`[data-unit="${unit}"]`);
        if (box) box.textContent = String(value).padStart(2, '0');
      }
      if (!left) {
        clearInterval(this.timer);
        if (this.dataset.hideWhenDone !== undefined) this.hidden = true;
      }
    }
  }
  if (!customElements.get('sale-countdown')) customElements.define('sale-countdown', SaleCountdown);

  // "remind me at 8am": email to a Klaviyo list (client API, same as the pop-up and password page)
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-sale-remind]');
    if (!form) return;
    event.preventDefault();
    const message = form.querySelector('[data-sale-message]');
    const email = form.querySelector('input[type="email"]').value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.textContent = 'please enter a valid email.';
      return;
    }
    const button = form.querySelector('button');
    button.disabled = true;
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${form.dataset.key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { custom_source: form.dataset.source || 'sale reminder', profile: { data: { type: 'profile', attributes: { email } } } },
            relationships: { list: { data: { type: 'list', id: form.dataset.list } } },
          },
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      form.querySelector('[data-sale-fields]').hidden = true;
      message.textContent = form.dataset.success || "you're on the list. see you at 8am.";
    } catch {
      message.textContent = 'something went wrong. try again.';
      button.disabled = false;
    }
  });

  document.addEventListener('click', async (event) => {
    // Announcement video
    const play = event.target.closest('[data-sale-video]');
    if (play) {
      const dialog = document.getElementById(play.dataset.saleVideo);
      dialog?.showModal();
      dialog?.querySelector('video')?.play();
      return;
    }
    const close = event.target.closest('[data-sale-video-close]');
    if (close) {
      const dialog = close.closest('dialog');
      dialog.querySelector('video')?.pause();
      dialog.close();
      return;
    }

    // "+ add to set" on a product card: send the chosen size to the page's bundle builder
    const setAdd = event.target.closest('[data-set-add]');
    if (setAdd) {
      const card = setAdd.closest('[data-set-card]');
      const option = card.querySelector('[data-set-variant]').selectedOptions[0];
      document.dispatchEvent(
        new CustomEvent('bundle:add', {
          detail: { id: Number(option.value), title: card.dataset.title, variant: option.textContent.trim(), price: Number(option.dataset.price), image: option.dataset.image },
        })
      );
      setAdd.textContent = 'added';
      setTimeout(() => (setAdd.textContent = setAdd.dataset.label), 1500);
      return;
    }

    // "add both" on a free-tee pair
    const pairAdd = event.target.closest('[data-pair-add]');
    if (pairAdd) {
      const pair = pairAdd.closest('[data-pair]');
      const items = [...pair.querySelectorAll('[data-pair-variant]')].map((s) => ({ id: Number(s.value), quantity: 1 }));
      pairAdd.disabled = true;
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: ['cart-drawer'] }),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.description || 'could not add');
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { html: result.sections?.['cart-drawer'] } }));
      } catch (error) {
        pair.querySelector('[data-pair-error]').textContent = error.message.toLowerCase();
      } finally {
        pairAdd.disabled = false;
      }
    }
  });

  window.sundaySale = { now, applyWindows };
  applyWindows();
  document.addEventListener('shopify:section:load', (e) => applyWindows(e.target));
})();
